import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Voter, VoterGroup } from '../../types';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where
} from 'firebase/firestore';

import VoterGroupCard from './VoterGroupCard';
import VoterGroupFilterDialog from './VoterGroupFilterDialog';
import { db } from '../../firebase';
import useAuth from '../../hooks/useAuth';

interface VoterGroupListControlProps {
  onEdit?: (voterGroup: VoterGroup) => void;
  onDelete?: (voterGroup: VoterGroup) => void;
  onSelect?: (voterGroup: VoterGroup) => void;
  // Optional filter to exclude certain groups
  excludeGroups?: string[];
  // Optional filter for group levels
  levelFilter?: VoterGroup['level'][];
}

const VoterGroupListControl: React.FC<VoterGroupListControlProps> = ({ 
  onEdit, 
  onDelete, 
  onSelect,
  excludeGroups = [],
  levelFilter
}) => {
  const { user } = useAuth();

  const [voterGroups, setVoterGroups] = useState<VoterGroup[]>([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<VoterGroup | null>(null);
  
  // New state for filter dialog
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [selectedMunicipality, setSelectedMunicipality] = useState('');
  const [selectedBarangay, setSelectedBarangay] = useState('');
  const [selectedLeader, setSelectedLeader] = useState<Voter | null>(null);
  const [selectedGroupLevel, setSelectedGroupLevel] = useState<VoterGroup['level'] | ''>('');
  
  const [isDialogActionDisabled, setIsDialogActionDisabled] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [loadNext, setLoadNext] = useState(false);
  const [lastGroupRef, setLastGroupRef] = useState<any>(null);

  const PAGE_SIZE = 20;

  type VoterGroupsPage = {
    groups: VoterGroup[];
    hasMore: boolean;
    lastSnapshotDoc: any;
  };

  const fetchVoterGroups = async (
    searchAfter: any
  ): Promise<VoterGroupsPage> => {
    if (!hasMore && searchAfter) {
      return {
        groups: [],
        hasMore: false,
        lastSnapshotDoc: null
      };
    }

    const voterGroupsCollection = collection(db, 'voterGroups');
    let voterGroupsQuery = query(voterGroupsCollection, orderBy('name'), limit(PAGE_SIZE));

    // Apply municipality filter
    if (selectedMunicipality) {
      voterGroupsQuery = query(voterGroupsQuery, where('city_municipality', '==', selectedMunicipality));
    }

    // Apply barangay filter
    if (selectedBarangay) {
      voterGroupsQuery = query(voterGroupsQuery, where('barangay', '==', selectedBarangay));
    }

    // Apply leader filter
    if (selectedLeader) {
      voterGroupsQuery = query(voterGroupsQuery, where('leader.id', '==', selectedLeader.id));
    }

    // Apply group level filter
    if (selectedGroupLevel) {
      voterGroupsQuery = query(voterGroupsQuery, where('level', '==', selectedGroupLevel));
    }

    if (searchAfter && voterGroups.length > 0) {
      voterGroupsQuery = query(voterGroupsQuery, startAfter(searchAfter));
    }

    // Apply level filter if provided
    if (levelFilter && levelFilter.length > 0) {
      voterGroupsQuery = query(voterGroupsQuery, where('level', 'in', levelFilter));
    }

    const querySnapshot = await getDocs(voterGroupsQuery);
    const newGroups = querySnapshot.docs
      .map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as VoterGroup[];

    return {
      groups: newGroups.filter(group => !excludeGroups.includes(group.id)),
      hasMore: newGroups.length >= PAGE_SIZE,
      lastSnapshotDoc: newGroups.length > 0 ? querySnapshot.docs[querySnapshot.docs.length - 1] : searchAfter
    };
  };

  useEffect(() => {
    setLoading(true);
    fetchVoterGroups(null).then((result) => {
      setVoterGroups(result.groups); 
      setHasMore(result.hasMore);
      setLastGroupRef(result.lastSnapshotDoc);
      setLoading(false);
    });
  }, [selectedMunicipality, selectedBarangay, selectedLeader, selectedGroupLevel]);
  
  useEffect(() => {
    if (loadNext) {
      setLoading(true);
      fetchVoterGroups(lastGroupRef).then((result) => {
        setVoterGroups(prevGroups => [...prevGroups, ...result.groups]); 
        setHasMore(result.hasMore);
        setLastGroupRef(result.lastSnapshotDoc);
        setLoading(false);
      });
      setLoadNext(false);
    }
  }, [loadNext]);
  
  const observer = useRef<IntersectionObserver | null>(null);
  
  const lastGroupElementRef = useCallback((node: HTMLDivElement) => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore) {
        setLoadNext(true);
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, hasMore]);

  const handleDeleteConfirmOpen = (group: VoterGroup) => {
    setGroupToDelete(group);
    setDeleteConfirmOpen(true);
    setIsDialogActionDisabled(false);
  };

  const handleDeleteConfirmClose = () => {
    setDeleteConfirmOpen(false);
    setGroupToDelete(null);
    setIsDialogActionDisabled(false);
  };

  const handleDelete = async () => {
    if (groupToDelete) {
      setIsDialogActionDisabled(true);
      
      try {
        await deleteDoc(doc(db, 'voterGroups', groupToDelete.id));
        onDelete?.(groupToDelete);
        setVoterGroups(voterGroups.filter((group) => group.id !== groupToDelete.id));
        handleDeleteConfirmClose();
      } catch (error) {
        console.error('Error deleting voter group:', error);
        setIsDialogActionDisabled(false);
      }
    }
  };

  const handleFilterDialogOpen = () => {
    setFilterDialogOpen(true);
  };

  const handleFilterDialogClose = () => {
    setFilterDialogOpen(false);
  };

  const handleApplyFilter = (filters: {
    municipality: string;
    barangay: string;
    leader: Voter | null;
    groupLevel: VoterGroup['level'] | '';
  }) => {
    setSelectedMunicipality(filters.municipality);
    setSelectedBarangay(filters.barangay);
    setSelectedLeader(filters.leader);
    setSelectedGroupLevel(filters.groupLevel);
  };

  const handleClearFilter = () => {
    setSelectedMunicipality('');
    setSelectedBarangay('');
    setSelectedLeader(null);
    setSelectedGroupLevel('');
  };

  // Function to render active filters
  const renderActiveFilters = () => {
    const activeFilters: { label: string, onDelete: () => void }[] = [];

    if (selectedMunicipality) {
      activeFilters.push({
        label: `Municipality: ${selectedMunicipality}`, 
        onDelete: () => setSelectedMunicipality('')
      });
    }

    if (selectedBarangay) {
      activeFilters.push({
        label: `Barangay: ${selectedBarangay}`, 
        onDelete: () => setSelectedBarangay('')
      });
    }

    if (selectedLeader) {
      activeFilters.push({
        label: `Leader: ${selectedLeader.name}`, 
        onDelete: () => setSelectedLeader(null)
      });
    }

    if (selectedGroupLevel) {
      activeFilters.push({
        label: `Level: ${selectedGroupLevel}`, 
        onDelete: () => setSelectedGroupLevel('')
      });
    }

    return activeFilters.length > 0 ? (
      <Box mb={2}>
        <Typography variant="subtitle1" gutterBottom>
          Active Filters
        </Typography>
        <Stack direction="row" spacing={1}>
          {activeFilters.map((filter, index) => (
            <Chip 
              key={index} 
              label={filter.label} 
              onDelete={filter.onDelete}
            />
          ))}
          {activeFilters.length > 1 && (
            <Chip 
              label="Clear All" 
              onDelete={handleClearFilter}
              color="primary"
            />
          )}
        </Stack>
      </Box>
    ) : null;
  };

  return (
    <>
      {user && (
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            {renderActiveFilters()}
          </Box>
          <Button variant="outlined" onClick={handleFilterDialogOpen}>
            Filter
          </Button>
        </Box>
      )}

      {user && voterGroups.map((group, index) => (
        <VoterGroupCard 
          key={group.id}
          group={group}
          onSelect={onSelect}
          onEdit={onEdit}
          onDelete={() => handleDeleteConfirmOpen(group)}
          user={user}
          isLastGroup={index === voterGroups.length - 1}
          lastGroupRef={index === voterGroups.length - 1 ? lastGroupElementRef : undefined}
        />
      ))}

      {loading && <Box display="flex" justifyContent="center"><CircularProgress /></Box>}
      {!loading && voterGroups.length === 0 && <Typography align="center">No Voters Group available</Typography>}
      {!loading && !hasMore && voterGroups.length > 0 && <Typography align="center">No more voters group to load. Total groups: {voterGroups.length}</Typography>}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteConfirmClose}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the voter group "{groupToDelete?.name}"?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleDeleteConfirmClose} 
            disabled={isDialogActionDisabled}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDelete} 
            color="error" 
            disabled={isDialogActionDisabled}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filter Dialog */}
      <VoterGroupFilterDialog 
        open={filterDialogOpen}
        onClose={handleFilterDialogClose}
        onApplyFilter={handleApplyFilter}
        onClearFilter={handleClearFilter}
      />
    </>
  );
};

export default VoterGroupListControl;
